import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingUp, Users, Shield, Calendar, Sparkles, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';

const serviceIcons = [Users, TrendingUp, Sparkles, Calendar, Shield, CheckCircle];
const FADE_IN = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const FADE_IN_DELAY_1 = { ...FADE_IN, transition: { duration: 0.6, delay: 0.1 } };
const FADE_IN_DELAY_2 = { ...FADE_IN, transition: { duration: 0.6, delay: 0.2 } };

const Home = () => {
  const { lang } = useLanguage();
  const h = t('home', lang);
  const fadeIn = FADE_IN;

  return (
    <div className="bg-white">
      {/* Hero Video */}
      <section className="w-full pt-20" data-testid="hero-banner-section">
        <div className="w-full aspect-[2/1] md:aspect-[3.5/1] overflow-hidden bg-[#0A1312]">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/hero_poster.jpg"
            className="w-full h-full object-cover"
          >
            <source src="/hero_video.mp4" type="video/mp4" />
          </video>
        </div>
      </section>

      {/* Content section with existing hero text */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeIn}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1312] mb-6 leading-tight" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}} data-testid="hero-headline">
                {h.heroTitle} <span className="text-[#65c3ba]">{h.heroHighlight}</span> {h.heroTitle2}
              </h1>
              <p className="text-base md:text-lg text-[#0A1312] mb-4 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {h.heroText} <strong className="text-[#65c3ba]">{h.heroBold1}</strong> {h.heroText2} <strong className="text-[#65c3ba]">{h.heroBold2}</strong> {h.heroText3}
              </p>
              <p className="text-base md:text-lg text-[#0A1312] mb-8" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {h.heroBottom} <strong className="text-[#65c3ba]">{h.heroBold3}</strong>.
              </p>
              <Link to="/valutazione-gratuita">
                <Button data-testid="hero-cta-button" className="bg-[#65c3ba] text-white hover:bg-[#3e716c] rounded-sm px-8 py-6 text-lg group">
                  {h.heroCta}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div {...FADE_IN_DELAY_2} className="relative">
              <img src="https://customer-assets.emergentagent.com/job_homewise-ticino/artifacts/peffgbo8_IMG-20260411-WA0090.jpg" alt="Luxury interior" className="rounded-sm w-full h-auto shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F7F9F9]" data-testid="key-numbers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div {...fadeIn} className="bg-white p-12 border border-[#E2E8F0] rounded-sm">
              <div className="text-5xl font-bold text-[#65c3ba] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.stat1}</div>
              <p className="text-lg text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>{h.stat1Text}</p>
            </motion.div>
            <motion.div {...FADE_IN_DELAY_1} className="bg-white p-12 border border-[#E2E8F0] rounded-sm">
              <div className="text-5xl font-bold text-[#65c3ba] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.stat2}</div>
              <p className="text-lg text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>{h.stat2Text}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="problem-solution-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.problemTitle}</h2>
              <div className="space-y-4 text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {h.problemItems.map((item, i) => (
                  <p key={`problem-${item.slice(0, 20)}`} className="flex items-start"><span className="text-[#65c3ba] mr-3 mt-1">&bull;</span><span>{item}</span></p>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.solutionTitle}</h2>
              <div className="space-y-4 text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>
                {h.solutionItems.map((item, i) => (
                  <p key={`solution-${item.slice(0, 20)}`} className="flex items-start"><CheckCircle className="text-[#65c3ba] mr-3 mt-1 flex-shrink-0" size={20} /><span>{item}</span></p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 bg-fixed bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1617637651279-533d3d379472?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxMdWdhbm8lMjBTd2l0emVybGFuZCUyMGFlcmlhbCUyMGNpdHlzY2FwZSUyMGxha2V8ZW58MHx8fHwxNzc2MDI5ODk2fDA&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-white/85" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.servicesTitle}</h2>
            <p className="text-base md:text-lg text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>{h.servicesSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {h.services.map((service, idx) => {
              const Icon = serviceIcons[idx];
              return (
                <motion.div key={service.title} {...fadeIn} transition={{ delay: idx * 0.1 }} className="bg-white p-8 border border-[#E2E8F0] rounded-sm hover:-translate-y-1 transition-transform" data-testid={`service-card-${idx}`}>
                  <Icon className="text-[#65c3ba] mb-4" size={32} strokeWidth={1.5} />
                  <h3 className="text-xl font-semibold text-[#0A1312] mb-3" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{service.title}</h3>
                  <p className="text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>{service.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src="https://images.pexels.com/photos/26925301/pexels-photo-26925301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Lugano" className="rounded-sm w-full h-auto shadow-lg" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.whyTitle}</h2>
              <p className="text-[#0A1312] mb-6" style={{fontFamily: "'Satoshi', sans-serif"}}>{h.whyIntro}</p>
              <div className="space-y-6">
                {h.whyItems.map((item, idx) => (
                  <div key={item.title} className="flex items-start">
                    <CheckCircle className="text-[#65c3ba] mr-3 mt-1 flex-shrink-0" size={22} />
                    <div>
                      <h3 className="text-lg font-semibold text-[#0A1312] mb-1" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{item.title}</h3>
                      <p className="text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1678895223308-da40c609e39b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHx3YXJtJTIwbW9kZXJuJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBjb3p5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3NzYwMzA1NTZ8MA&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-[#3e716c]/80" />
        <div className="max-w-4xl mx-auto text-center relative z-10 text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{h.ctaTitle}</h2>
          <p className="text-base md:text-lg text-white/70 mb-8" style={{fontFamily: "'Satoshi', sans-serif"}}>{h.ctaText}</p>
          <Link to="/valutazione-gratuita">
            <Button data-testid="final-cta-button" className="bg-[#65c3ba] text-white hover:bg-white hover:text-[#3e716c] rounded-sm px-8 py-6 text-lg">{h.ctaButton}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;